import React, { MouseEvent, useEffect, useState } from 'react';
import {
    Button,
    makeStyles,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    Overflow,
    OverflowItem,
    Tab,
    TabList,
    Tooltip,
    useIsOverflowItemVisible,
    useOverflowMenu,
} from '@fluentui/react-components';
import {
    ChevronDoubleLeft16Regular,
    ChevronDoubleLeftRegular,
    ChevronDoubleRight16Regular,
    ChevronDoubleRightRegular,
    MoreHorizontalRegular,
    PanelLeftContractRegular,
    PanelLeftExpandRegular,
} from '@fluentui/react-icons';
import { useCommonStyles } from './Styles/commonStyles';
import { useMediaSizes } from './Styles/useMediaSizes';
import { Separator } from '@fluentui/react';
import { Column } from './Column';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
    },
    tabList: {
        width: '100%',
        minWidth: 0,
        flexWrap: 'nowrap',
        overflow: 'hidden',
    },
    overflowMenuButton: {
        alignSelf: 'center',
    },
});

type CWTab = {
    isSeparator?: boolean;
    value?: string;
    icon?: JSX.Element;
    label?: string;
    onClick?: () => void;
    access?: boolean;
};

type CWTabListProps = {
    selectedTab: string;
    vertical?: boolean;
    collapsed?: boolean;
    collapsible?: boolean;
    overflow?: boolean;
    size?: 'small' | 'medium' | 'large';
    dataTestId?: string;
    tabs?: CWTab[];
};
export const CWTabList = (props: CWTabListProps) => {
    const {
        selectedTab,
        vertical = false,
        collapsed = false,
        collapsible = false,
        overflow = true,
        size,
        dataTestId,
        tabs,
    } = props;

    //Hooks
    const mediaSizes = useMediaSizes();
    const [isResponsiveCollapsed, setIsResponsiveCollapsed] = useState(() => mediaSizes.isUnderLaptop);

    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const tabClasses = mergeClasses(commonStyles.button.root);
    const isResponsiveCollapseEnabled = collapsible && mediaSizes.isUnderLaptop;
    const isCollapsed = isResponsiveCollapseEnabled ? isResponsiveCollapsed : collapsed;

    useEffect(() => {
        if (isResponsiveCollapseEnabled) {
            setIsResponsiveCollapsed(true);
        }
    }, [isResponsiveCollapseEnabled]);

    const getUncontrolledSize = () => {
        const sizeFromMedia = mediaSizes.isUnderLaptop ? 'small' : mediaSizes.isUnderDesktop ? 'medium' : 'large';
        return sizeFromMedia;
    };

    const handleTabClick = (clickEvent?: () => void) => (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        clickEvent?.();
    };

    //const visibleTabs = tabs?.filter((tab) => tab.access !== false && !tab.isSeparator) ?? [];

    // const handleDropdownOptionChanged = (value: string) => {
    //     const tab = visibleTabs.find((t) => t.value === value);
    //     tab?.onClick?.();
    // };

    // if (collapsed) {
    //     return (
    //         <DropdownMenu
    //             initialValue={selectedTab}
    //             appearance='underline'
    //             options={visibleTabs.map((tab) => ({
    //                 label: tab.label ?? '',
    //                 value: tab.value ?? '',
    //                 icon: tab.icon,
    //             }))}
    //             onOptionChanged={handleDropdownOptionChanged}
    //             size={size || getUncontrolledSize()}
    //             textSize={400}
    //             textWeight='bold'
    //             dataTestId={`tab-list-${dataTestId || 'default'}`}
    //         />
    //     );
    // }

    const useOverflow = overflow && !vertical;
    const accessibleTabs = tabs?.filter((tab) => tab.access !== false) ?? [];

    const renderTab = (tab: CWTab) => (
        <Tooltip content={tab.label || ''} relationship='label'>
            <Tab
                className={tabClasses}
                value={tab.value}
                icon={tab.icon}
                onClick={handleTabClick(tab.onClick)}
                data-testid={`tab-${dataTestId}-${tab.value}`}
            >
                {!isCollapsed && <>{tab.label}</>}
            </Tab>
        </Tooltip>
    );

    const tabList = (
        <TabList
            className={useOverflow ? styles.tabList : undefined}
            selectedValue={selectedTab}
            vertical={vertical}
            // size={size || getUncontrolledSize()}
            data-testid={`tab-list-${dataTestId || 'default'}`}
        >
            {accessibleTabs.map((tab, idx) => (
                <React.Fragment key={tab.value || idx}>
                    {tab.isSeparator ? (
                        <Separator />
                    ) : useOverflow ? (
                        <OverflowItem id={tab.value ?? `${idx}`} priority={tab.value === selectedTab ? 1 : 0}>
                            {renderTab(tab)}
                        </OverflowItem>
                    ) : (
                        renderTab(tab)
                    )}
                </React.Fragment>
            ))}
            {useOverflow && (
                <TabOverflowMenu className={styles.overflowMenuButton} tabs={accessibleTabs} dataTestId={dataTestId} />
            )}
        </TabList>
    );

    const responsiveTabList = useOverflow ? (
        <Overflow minimumVisible={1} padding={48}>
            {tabList}
        </Overflow>
    ) : (
        tabList
    );

    if (!isResponsiveCollapseEnabled) {
        return responsiveTabList;
    }

    const toggleLabel = isCollapsed ? 'Expand tabs' : 'Collapse tabs';

    return (
        <Column noGap>
            <Column height='auto' align='right'>
                <Tooltip content={toggleLabel} relationship='label'>
                    <Button
                        size='small'
                        appearance='subtle'
                        icon={
                            isCollapsed ? (
                                <ChevronDoubleRightRegular style={{ fontSize: '.8em' }} />
                            ) : (
                                <ChevronDoubleLeftRegular style={{ fontSize: '.8em' }} />
                            )
                        }
                        aria-label={toggleLabel}
                        aria-expanded={!isCollapsed}
                        onClick={() => setIsResponsiveCollapsed((current) => !current)}
                        data-testid={`tab-list-toggle-${dataTestId || 'default'}`}
                    />
                </Tooltip>
            </Column>
            {responsiveTabList}
        </Column>
    );
};

type TabOverflowMenuItemProps = {
    tab: CWTab;
    dataTestId?: string;
};
const TabOverflowMenuItem = (props: TabOverflowMenuItemProps) => {
    const { tab, dataTestId } = props;
    const isVisible = useIsOverflowItemVisible(tab.value ?? '');

    if (isVisible) {
        return null;
    }

    return (
        <MenuItem
            icon={tab.icon}
            onClick={() => tab.onClick?.()}
            data-testid={`tab-overflow-item-${dataTestId}-${tab.value}`}
        >
            {tab.label}
        </MenuItem>
    );
};

type TabOverflowMenuProps = {
    tabs: CWTab[];
    dataTestId?: string;
    className?: string;
};
const TabOverflowMenu = (props: TabOverflowMenuProps) => {
    const { tabs, dataTestId, className } = props;
    const { ref, isOverflowing, overflowCount } = useOverflowMenu<HTMLButtonElement>();

    if (!isOverflowing) {
        return null;
    }

    return (
        <Menu>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip content={`${overflowCount} more`} relationship='label'>
                    <Button
                        ref={ref}
                        className={className}
                        appearance='subtle'
                        icon={<MoreHorizontalRegular />}
                        aria-label={`${overflowCount} more tabs`}
                        data-testid={`tab-overflow-button-${dataTestId || 'default'}`}
                    />
                </Tooltip>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {tabs
                        .filter((tab) => !tab.isSeparator)
                        .map((tab, idx) => (
                            <TabOverflowMenuItem key={tab.value || idx} tab={tab} dataTestId={dataTestId} />
                        ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};
